import { useEffect } from 'react';
import { signalRService } from '@/services/signalRService';
import logger from '@/lib/logger';
import { testService, type TestDetailResponse } from '@/services/api/testService';
import type { MeasurementPhase } from '@/features/measurement/hooks/useSpecimenMeasurement';

/**
 * Subscribes to all real-time classification and status updates for a test.
 *
 * @param testId            - The test to subscribe to.
 * @param phase             - Updates only run when phase is 'completed'.
 * @param setMeasurementResults - State setter for channel result updates.
 * @param setLoadedTest     - State setter for test-level status changes.
 */
export function useClassificationUpdates(
  testId: string | null | undefined,
  phase: MeasurementPhase,
  setMeasurementResults: React.Dispatch<React.SetStateAction<any>>,
  setLoadedTest: React.Dispatch<React.SetStateAction<TestDetailResponse | null>>,
): void {
  // Channel-level classification results
  useEffect(() => {
    if (!testId || phase !== 'completed') return;

    let unsubscribeChannel: (() => void) | null = null;
    let unsubscribeRetrying: (() => void) | null = null;
    let unsubscribeFailed: (() => void) | null = null;
    let unsubscribeReconnected: (() => void) | null = null;
    let cancelled = false;

    const reconcile = async () => {
      try {
        const test = await testService.getTestById(testId);
        if (cancelled) return;
        setMeasurementResults((prev: any) => {
          if (!prev) return prev;
          return {
            ...prev,
            channels: prev.channels.map((ch: any) => {
              if (ch.result !== 'ANALYZING') return ch;
              const match = test.pathogenClassificationResults?.find(
                (r) => r.channelId === ch.channel,
              );
              if (!match || match.status == null) return ch;
              return {
                ...ch,
                result: match.status.toUpperCase(),
                impedance: match.concentration || ch.impedance,
                confidence: match.confidence,
              };
            }),
          };
        });
      } catch (error) {
        logger.error('[ClassificationUpdates] Reconciliation fetch failed:', error);
      }
    };

    const setupSignalR = async () => {
      try {
        await signalRService.connect();

        unsubscribeChannel = signalRService.subscribeToChannelClassification(
          testId,
          (update) => {
            setMeasurementResults((prev: any) => {
              if (!prev) return prev;
              return {
                ...prev,
                channels: prev.channels.map((ch: any) =>
                  ch.channel === update.channelId
                    ? {
                        ...ch,
                        result: update.status.toUpperCase(),
                        impedance: update.concentration || ch.impedance,
                        confidence: update.confidence,
                      }
                    : ch,
                ),
              };
            });
          },
        );

        reconcile();
        unsubscribeReconnected = signalRService.onReconnected(reconcile);

        unsubscribeRetrying = signalRService.subscribeToTestClassificationRetrying(
          testId,
          (update) => {
            logger.warn(
              `[ClassificationUpdates] Retrying (${update.attemptNumber}/${update.maxAttempts}):`,
              update.error,
            );
            setMeasurementResults((prev: any) => {
              if (!prev) return prev;
              return {
                ...prev,
                classificationError: update.error,
                retryAttempt: update.attemptNumber,
                maxRetries: update.maxAttempts,
                channels: prev.channels.map((ch: any) =>
                  ch.result === 'ANALYZING' ? { ...ch, result: 'RETRYING' } : ch,
                ),
              };
            });
          },
        );

        unsubscribeFailed = signalRService.subscribeToTestClassificationFailed(
          testId,
          (update) => {
            logger.error('[ClassificationUpdates] Permanently failed:', update.error);
            setMeasurementResults((prev: any) => {
              if (!prev) return prev;
              return {
                ...prev,
                classificationError: update.error,
                classificationFailed: true,
                channels: prev.channels.map((ch: any) =>
                  ch.result === 'ANALYZING' || ch.result === 'RETRYING'
                    ? { ...ch, result: 'ERROR' }
                    : ch,
                ),
              };
            });
          },
        );
      } catch (error) {
        // Never fabricate a diagnostic result. Without live updates the operator is told so and
        // the stored result is retrieved by the reconcile-on-load path (page reload / reconnect).
        logger.error('[ClassificationUpdates] Failed to set up SignalR — live result updates unavailable', error);
        setMeasurementResults((prev: any) => {
          if (!prev) return prev;
          return {
            ...prev,
            classificationError:
              'Live result updates are unavailable. Reload the page to retrieve the result.',
          };
        });
      }
    };

    setupSignalR();
    return () => {
      cancelled = true;
      unsubscribeChannel?.();
      unsubscribeRetrying?.();
      unsubscribeFailed?.();
      unsubscribeReconnected?.();
    };
  }, [testId, phase, setMeasurementResults]);

  // Test-level status updates
  useEffect(() => {
    if (!testId) return;
    let unsubscribe: (() => void) | null = null;
    const setup = async () => {
      try {
        await signalRService.connect();
        unsubscribe = signalRService.subscribeToTestStatusChange(testId, (update) => {
          logger.debug('[ClassificationUpdates] Test status changed:', update.status);
          setLoadedTest((prev) => (prev ? { ...prev, status: update.status } : prev));
        });
      } catch (error) {
        logger.error('[ClassificationUpdates] Failed to subscribe to test status changes:', error);
      }
    };
    setup();
    return () => {
      unsubscribe?.();
    };
  }, [testId, setLoadedTest]);
}
